import { getAdminBranding, saveAdminBranding } from '../api/client.js';
import { WELCOME_MESSAGES } from './chat.js';
import { PRIVACY_SENTENCE, PRIVACY_URL_PLACEHOLDER } from '../utils/privacy.js';

// DOM elements
let scopeSelect, appNameInput, taglineInput, welcomeTextInput, privacyUrlInput;
let productNameInput, supportContactInput;
let saveBtn, resetBtn, statusEl, kbRow, kbListEl;

// Editor data from the backend: [{scope, stored, effective, collections?}]
let scopeData = [];
// All existing KBs: [{name, label}]
let availableKbs = [];

const SCOPE_LABELS = { default: 'Standard (alle Gruppen)' };

export function initBrandingAdmin() {
  scopeSelect = document.getElementById('brandingScopeSelect');
  appNameInput = document.getElementById('brandingAppName');
  taglineInput = document.getElementById('brandingTagline');
  welcomeTextInput = document.getElementById('brandingWelcomeText');
  privacyUrlInput = document.getElementById('brandingPrivacyUrl');
  productNameInput = document.getElementById('brandingProductName');
  supportContactInput = document.getElementById('brandingSupportContact');
  saveBtn = document.getElementById('brandingSaveBtn');
  resetBtn = document.getElementById('brandingResetBtn');
  statusEl = document.getElementById('brandingStatus');
  kbRow = document.getElementById('brandingKbRow');
  kbListEl = document.getElementById('brandingKbList');

  if (!scopeSelect) return;

  scopeSelect.addEventListener('change', fillFormForSelectedScope);
  saveBtn.addEventListener('click', () => handleSave(false));
  resetBtn.addEventListener('click', () => handleSave(true));
}

export async function loadBrandingAdmin() {
  if (!scopeSelect) return;
  try {
    const data = await getAdminBranding();
    scopeData = data.scopes || [];
    availableKbs = data.available_kbs || [];

    const previous = scopeSelect.value;
    scopeSelect.innerHTML = '';
    scopeData.forEach(({ scope }) => {
      const option = document.createElement('option');
      option.value = scope;
      option.textContent = SCOPE_LABELS[scope] || scope;
      scopeSelect.appendChild(option);
    });
    if (previous && scopeData.some(({ scope }) => scope === previous)) {
      scopeSelect.value = previous;
    }
    fillFormForSelectedScope();
  } catch (error) {
    showStatus(`Fehler: ${error.message}`, 'error');
  }
}

function fillFormForSelectedScope() {
  const entry = scopeData.find(({ scope }) => scope === scopeSelect.value);
  if (!entry) return;
  // Pre-fill with the values currently in effect for this scope. The built-in
  // start text lives in the frontend (audience-dependent), so when no override
  // is configured, show the end-user default the visitor would actually see.
  appNameInput.value = entry.effective.app_name || '';
  taglineInput.value = entry.effective.tagline || '';
  welcomeTextInput.value = entry.effective.welcome_text || WELCOME_MESSAGES.end_user;
  // Pre-fill with the default sentence; the admin only replaces the URL.
  privacyUrlInput.value =
    entry.effective.privacy_url || PRIVACY_SENTENCE(PRIVACY_URL_PLACEHOLDER);
  if (productNameInput) productNameInput.value = entry.effective.product_name || '';
  if (supportContactInput) supportContactInput.value = entry.effective.support_contact || '';
  renderKbCheckboxes(entry);
}

function renderKbCheckboxes(entry) {
  if (!kbRow || !kbListEl) return;
  // KB assignment applies to group scopes only.
  if (entry.scope === 'default' || !entry.collections) {
    kbRow.style.display = 'none';
    kbListEl.innerHTML = '';
    return;
  }

  kbRow.style.display = '';
  kbListEl.innerHTML = '';
  const assigned = new Set(entry.collections.effective || []);
  availableKbs.forEach((kb) => {
    const label = document.createElement('label');
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.value = kb.name;
    checkbox.checked = assigned.has(kb.name);
    label.appendChild(checkbox);
    label.appendChild(document.createTextNode(` ${kb.label}`));
    if (kb.label !== kb.name) {
      const hint = document.createElement('span');
      hint.className = 'kb-name-hint';
      hint.textContent = `(${kb.name})`;
      label.appendChild(hint);
    }
    kbListEl.appendChild(label);
  });
}

function checkedKbs() {
  if (!kbListEl) return [];
  return Array.from(kbListEl.querySelectorAll('input[type="checkbox"]:checked')).map(
    (checkbox) => checkbox.value
  );
}

async function handleSave(reset) {
  const scope = scopeSelect.value;
  if (!scope) return;

  // Default scope only: an unchanged pre-filled built-in start text is not an
  // override — saving it would freeze the audience-dependent default welcome
  // messages. For group scopes, Speichern means "use exactly what I see".
  const entry = scopeData.find((item) => item.scope === scope);
  let welcomeText = welcomeTextInput.value.trim();
  const hadStoredWelcome = Boolean(entry && entry.stored.welcome_text);
  if (
    scope === 'default' &&
    !hadStoredWelcome &&
    welcomeText === WELCOME_MESSAGES.end_user.trim()
  ) {
    welcomeText = '';
  }

  const isGroupScope = Boolean(entry && entry.collections);

  // The pre-filled template still carrying the placeholder URL is not a real
  // value — storing it would render a dead "https://..." link in the chat.
  let privacyValue = privacyUrlInput.value.trim();
  if (privacyValue === PRIVACY_SENTENCE(PRIVACY_URL_PLACEHOLDER)) {
    privacyValue = '';
  }

  const values = reset
    ? {
        app_name: '',
        tagline: '',
        welcome_text: '',
        privacy_url: '',
        product_name: '',
        support_contact: '',
      }
    : {
        app_name: appNameInput.value.trim(),
        tagline: taglineInput.value.trim(),
        welcome_text: welcomeText,
        privacy_url: privacyValue,
        product_name: productNameInput ? productNameInput.value.trim() : '',
        support_contact: supportContactInput ? supportContactInput.value.trim() : '',
      };

  if (isGroupScope) {
    if (reset) {
      values.clear_collections = true;
    } else {
      const kbs = checkedKbs();
      if (kbs.length === 0) {
        showStatus('Bitte mindestens eine Wissensdatenbank auswählen.', 'error');
        return;
      }
      values.collections = kbs;
    }
  }

  saveBtn.disabled = true;
  resetBtn.disabled = true;
  try {
    const result = await saveAdminBranding(scope, values);
    if (entry) {
      entry.stored = result.stored;
      entry.effective = result.effective;
      if (result.collections) {
        entry.collections = result.collections;
      }
    }
    fillFormForSelectedScope();
    showStatus(
      reset
        ? 'Zurückgesetzt — Umgebungs-/Standardwerte gelten wieder.'
        : 'Gespeichert. Änderungen sind sofort wirksam.',
      'success'
    );
    // The running page may itself be affected (e.g. default scope) — let
    // main.js re-apply branding.
    window.dispatchEvent(new CustomEvent('branding-changed'));
  } catch (error) {
    showStatus(`Fehler: ${error.message}`, 'error');
  } finally {
    saveBtn.disabled = false;
    resetBtn.disabled = false;
  }
}

let statusTimer = null;
function showStatus(message, type) {
  if (statusTimer) clearTimeout(statusTimer);
  statusEl.textContent = message;
  statusEl.style.display = 'block';
  statusEl.className = `admin-upload-status status-${type}`;
  if (type === 'success') {
    statusTimer = setTimeout(() => {
      statusEl.style.display = 'none';
      statusTimer = null;
    }, 5000);
  }
}
